import { Box, Button, ButtonGroup, FormControlLabel, IconButton, Link, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useTheme } from "@mui/material"
import { AppId } from "../../../../app/appTypes"
import { useAppDispatch } from "../../../../app/hooks"
import { useCallback, useEffect, useMemo, useState } from "react"
import { DocumentTeamMember, loadDocumentTeamMembers } from "../../components/teamMemberApi"
import { unwrapResult } from "@reduxjs/toolkit"
import { ErrorContainer } from "../../../../app/ErrorContainer"
import { LoadingContainer } from "../../../../app/LoadingContainer"
import { Localized } from "@fluent/react"
import { If } from "../../../../app/If"
import { format, parseJSON } from "date-fns";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import produce from "immer"
import { DeleteTeamMemberDialog } from "../../components/teamMembers/DeleteTeamMemberDialog"
import { AddEditTeamMemberDialog } from "../../components/teamMembers/AddEditTeamMemberDialog"
import { DocumentContentActions } from "../../documentsApi"
import BadgeAvatar from "../../../mockups/BadgeAvatar"
import { TeamMemberRoleChip } from "../../components/teamMembers/TeamMemberRoleChip"
import { PrintPreviewTeamView } from "../../components/PrintPreviewTeamView"
import { ImportedDocumentDiscussionMembers } from "./ImportedDocumentDiscussionMembers"
import GroupsIcon from '@mui/icons-material/Groups';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

type DialogState
    = "none"
    | "addEditTeamMember"
    | "deleteTeamMember"
    | { tag: "confirmTeamMemberDelete", documentId: AppId, teamMemberId: AppId, user: string }


export interface ImportedDocumentTeamMemberViewProps {
    documentId: AppId
    discussionRelatedObjectId: string
    actions: DocumentContentActions[],
    enablePreview?: boolean
}

function hasAction(
    actions: DocumentContentActions[],
    action: DocumentContentActions
): boolean {
    return actions.includes(action);
}

export const ImportedDocumentTeamMemberView = ({ documentId, discussionRelatedObjectId, actions, enablePreview = false }: ImportedDocumentTeamMemberViewProps) => {
    const dispatch = useAppDispatch()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);
    const [teamMembers, setTeamMembers] = useState<DocumentTeamMember[]>();
    const teamMembersSelection = useMemo(() => new Set(teamMembers?.map(u => u.userName)), [teamMembers])

    const [dialog, setDialog] = useState<DialogState>("none")
    const [isPreview, setIsPreview] = useState(false);

    useEffect(() => {
        setIsLoading(true)
        dispatch(loadDocumentTeamMembers(documentId))
            .then(unwrapResult)
            .then(setTeamMembers)
            .catch((error) => { console.error(error); setIsError(true); setError(error); })
            .finally(() => setIsLoading(false))
    }, [])

    const handleDeleteTeamMemberClick = useCallback((entity: DocumentTeamMember, user: string) => {
        setDialog({ tag: "confirmTeamMemberDelete", documentId: documentId, teamMemberId: entity.id, user })

    }, [])

    const handleDeleteTMClick = useCallback((entity: DocumentTeamMember, user: string) => {
    }, [])

    const handleAddTeamMemberClick = useCallback(() => {
        setDialog("addEditTeamMember")
    }, [])

    const handleDialogCancelClick = useCallback(() => {
        setDialog("none")
    }, [])

    const onTeamMemberSuccessfulSave = (teamMember: DocumentTeamMember) => {
        setTeamMembers(
            produce(draft => {
                if (draft !== undefined) {
                    draft.push(teamMember)
                }
            })
        )
        setDialog("none")
    };

    const onTeamMemberSuccessfulDelete = (teamMemberId: AppId) => {
        setTeamMembers(
            produce(draft => {
                if (draft !== undefined) {
                    const index = draft.findIndex(t => t.id === teamMemberId)
                    if (index >= 0) {
                        draft.splice(index, 1)
                    }
                }
            })
        )
        setDialog("none")
    };


    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    return <>
        {enablePreview && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, mb: 1 }}>
                <FormControlLabel
                    control={<Switch checked={isPreview} onChange={() => setIsPreview(v => !v)} />}
                    label={<Localized id="print-preview">
                        <span>_preview</span>
                    </Localized>}
                />
            </Box>
        )}
        {enablePreview && isPreview ? (
            <PrintPreviewTeamView teamMembers={teamMembers ?? []} />
        ) : (
            <>
                <TableContainer component={Paper} sx={{ mb: 2, overflow: "hidden" }}>

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell colSpan={4} sx={{ bgcolor: "action.hover", borderBottom: "1px solid", borderColor: "divider", py: 0.75 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <GroupsIcon fontSize="medium" sx={{ mr: 1, color: "text.secondary" }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                                <Localized id="document-team">
                                                    <span>_team</span>
                                                </Localized>
                                            </Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                            </TableRow>
                            <TableRow >
                                <TableCell sx={{ width: 72, fontWeight: 600 }}>#</TableCell>
                                <TableCell sx={{
                                    minWidth: 200,
                                }}>
                                    <Localized id="user">
                                        <span>_user</span>
                                    </Localized>
                                </TableCell>
                                <TableCell sx={{ width: 180, fontWeight: 600 }}>
                                    <Localized id="user-role" vars={{ num: 0 }}>
                                        <span>_rola</span>
                                    </Localized>
                                </TableCell>
                                <TableCell sx={{
                                    width: 72,
                                }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {teamMembers?.map((tm, index) => (
                                <TeamMemberRow
                                    editable={true}
                                    actions={actions}
                                    entity={tm}
                                    index={index}
                                    key={tm.userName ?? index}
                                    onDeleteClick={handleDeleteTeamMemberClick}
                                />
                            ))}
                        </TableBody>

                    </Table>

                    <Box sx={{
                        px: 2,
                        py: 1,
                        borderTop: "1px solid",
                        borderColor: "divider",
                    }}>
                        <If condition={hasAction(actions, "AddTeamMember")}>
                            <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon />} onClick={handleAddTeamMemberClick}>
                                <Localized id="add">
                                    <span>_dodaj</span>
                                </Localized>
                            </Button>
                        </If>
                    </Box>

                </TableContainer>
                <ImportedDocumentDiscussionMembers relatedObjectId={discussionRelatedObjectId} />
            </>
        )}
        {
            dialog === "addEditTeamMember" &&
            <AddEditTeamMemberDialog
                documentId={documentId}
                mode={"create"}
                excludedUsers={teamMembersSelection}
                userToSubstitute={""}
                onCancelClick={handleDialogCancelClick}
                onSuccessfulSave={onTeamMemberSuccessfulSave}
            />
        }
        {typeof dialog === 'object' && dialog.tag === "confirmTeamMemberDelete" && <DeleteTeamMemberDialog
            documentId={dialog.documentId}
            teamMemberId={dialog.teamMemberId}
            userFullName={dialog.user}
            onSuccessfulSave={onTeamMemberSuccessfulDelete}
            onCancel={handleDialogCancelClick}
        />
        }
    </>
}

interface TeamMemberRowProps {
    entity: DocumentTeamMember
    actions: DocumentContentActions[]
    index: number
    onDeleteClick: (entity: DocumentTeamMember, user: string) => void
    editable: boolean
}

const TeamMemberRow = ({ entity, index, onDeleteClick, editable, actions }: TeamMemberRowProps) => {
    const { userName } = entity
    const { firstName, surname, position, companyName } = entity

    const deleteActive = actions.includes("DeleteTeamMember") === undefined && actions.includes("SubstituteTeamMember") === undefined && editable
    const deleteMemberActive = actions.includes("DeleteTeamMember") !== undefined

    const theme = useTheme();
    let substitutionRowColor = {};

    const hasSubstitution = entity.substitutionName !== null && entity.substitutionName !== ""

    if (hasSubstitution) {
        substitutionRowColor = { backgroundColor: theme.palette.grey[200] }
    }

    const handleDeleteClick = useCallback(() => {
        onDeleteClick(entity, `${firstName} ${surname}`)
    }, [entity, onDeleteClick])

    return <TableRow hover style={substitutionRowColor}>
        <TableCell sx={{ width: 72, verticalAlign: "top" }}>{index + 1}</TableCell>


        <TableCell sx={{ verticalAlign: "top" }}>
            <Box display="flex" alignItems="center" gap={1}>
                <BadgeAvatar
                    firstName={firstName}
                    surname={surname}
                    userId={entity.userId}
                />

                <Box display="flex" flexDirection="column" minWidth={0}>
                    <Typography
                        variant="body2"
                        fontWeight={500}
                        noWrap
                    >
                        {firstName} {surname}
                    </Typography>

                    {position && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ lineHeight: 1.3 }}
                        >
                            {position}
                        </Typography>
                    )}

                    <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{
                            lineHeight: 1.3,
                            mt: 0.2,
                            fontWeight: 500,
                        }}
                    >
                        {companyName || '-'}
                    </Typography>

                    {userName && (
                        <Link
                            href={`mailto:${userName}`}
                            underline="none"
                            title={userName}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.4,
                                minWidth: 0,
                                mt: 0.2,
                                color: 'text.disabled',
                                width: 'fit-content',
                                maxWidth: '100%',
                                cursor: 'pointer',

                                '&:hover': {
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            <EmailOutlinedIcon
                                sx={{
                                    fontSize: 13,
                                    color: 'inherit',
                                    flexShrink: 0,
                                }}
                            />

                            <Typography
                                variant="caption"
                                noWrap
                                sx={{
                                    lineHeight: 1.3,
                                    fontSize: '0.72rem',
                                    color: 'inherit',
                                }}
                            >
                                {userName}
                            </Typography>
                        </Link>
                    )}
                </Box>
            </Box>
        </TableCell>
        <TableCell sx={{ width: 180, verticalAlign: "top" }}>
            <TeamMemberRoleChip role={entity.role} />
        </TableCell>
        <TableCell sx={{ width: 72, verticalAlign: "top" }}>
            <ButtonGroup>
                <If condition={deleteActive}>
                    <Tooltip title={<Localized id="delete">Usuń</Localized>}>
                        <IconButton size="small" onClick={handleDeleteClick}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </If>
                <If condition={deleteMemberActive}>
                    <Tooltip title={<Localized id="delete">Usuń</Localized>}>
                        <IconButton size="small" onClick={handleDeleteClick}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </If>
            </ButtonGroup>
        </TableCell>
    </TableRow>
}

